import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import { useSession } from '../context/SessionContext';

const JoinSession = () => {
  const [sessionId, setSessionId] = useState('');
  const [errors, setErrors] = useState({});
  const [recentSessions, setRecentSessions] = useState([]);
  const { joinSession, loading, error } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    // Load recent sessions from localStorage
    const loadRecentSessions = () => {
      try {
        const saved = localStorage.getItem('recentSessions');
        if (saved) {
          const sessions = JSON.parse(saved);
          setRecentSessions(sessions);
        }
      } catch (err) {
        console.error('Failed to load recent sessions:', err);
      }
    };
    
    loadRecentSessions();
  }, []);

  const handleChange = (e) => {
    const { value } = e.target;
    setSessionId(value.toUpperCase());
    
    // Clear error when user types
    if (errors.sessionId) {
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!sessionId.trim()) {
      newErrors.sessionId = 'Session ID is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const joinedSession = await joinSession({ id: sessionId });
      
      // Ensure we have the required session data
      if (!joinedSession || !joinedSession.id || !joinedSession.name) {
        console.error('Invalid session data received:', joinedSession);
        throw new Error('Invalid session data received');
      }

      // Load existing sessions first to ensure we have the latest
      let currentSessions = [];
      try {
        const saved = localStorage.getItem('recentSessions');
        if (saved) {
          currentSessions = JSON.parse(saved);
        }
      } catch (err) {
        console.error('Failed to load existing sessions:', err);
      }
      
      // Add to recent sessions
      const updatedSessions = [
        {
          id: joinedSession.id,
          name: joinedSession.name,
          lastAccessed: new Date().toISOString()
        },
        ...currentSessions.filter(s => s.id !== joinedSession.id).slice(0, 4) // Keep last 5 sessions
      ];
      
      // Update state and localStorage
      setRecentSessions(updatedSessions);
      localStorage.setItem('recentSessions', JSON.stringify(updatedSessions));
      
      // Navigate to the session management page
      navigate(`/session/${joinedSession.id}/manage`);
    } catch (err) {
      console.error('Failed to join session:', err);
      // Error is handled by the context and displayed below
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout title="Join Session" backLink="/">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card title="Join an Existing Session">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                {error}
              </div>
            )}
            
            <Input
              label="Session ID"
              id="sessionId"
              name="sessionId"
              value={sessionId}
              onChange={handleChange}
              placeholder="Enter the session ID (e.g., PNPFL3KR)"
              error={errors.sessionId}
              required
              className="uppercase"
            />
            
            <div className="mt-6">
              <Button
                type="submit"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Joining...' : 'Join Session'}
              </Button>
            </div>
          </form>
        </Card>

        {recentSessions.length > 0 && (
          <Card title="Recent Sessions">
            <div className="divide-y divide-gray-200">
              {recentSessions.map((session) => (
                <div key={session.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{session.name}</h3>
                      <p className="text-sm text-gray-500">
                        ID: <span className="font-mono">{session.id}</span>
                      </p>
                      <p className="text-sm text-gray-500">Last accessed: {formatDate(session.lastAccessed)}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => navigate(`/session/${session.id}/display`)}
                        variant="secondary"
                        size="sm"
                      >
                        Display View
                      </Button>
                      <Button
                        onClick={() => navigate(`/session/${session.id}/manage`)}
                        size="sm"
                      >
                        Manage Questions
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default JoinSession; 